import { buildAccountTree } from "./accountTree";

/**
 * Opções de status usadas no filtro (modal) e na tabela.
 * O valor vazio ("") representa "todos".
 */
export const STATUS_OPTIONS = [
    { value: "pendente", label: "Pendente" },
    { value: "em_aberto", label: "Em aberto" },
    { value: "ok", label: "Conciliado" },
    { value: "incorreto", label: "Conciliado (valor errado)" },
    { value: "sem_valor", label: "Sem nenhum valor" },
];

export const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
}, {});

function toCents(value) {
    return Math.round(Number(value) * 100);
}

function getItens(row) {
    const itens = row?.conciliacao?.itens;
    return Array.isArray(itens) ? itens : [];
}

/**
 * Soma dos itens em centavos.
 * Itens.valor e valor_conciliado vêm da API como BIGINT (centavos).
 */
function getSomaCentavos(row) {
    const conciliacao = row?.conciliacao;
    if (!conciliacao) return null;

    const itens = getItens(row);
    if (itens.length > 0) {
        return itens.reduce((acc, item) => acc + Number(item.valor || 0), 0);
    }

    if (conciliacao.valor_conciliado != null) {
        return Number(conciliacao.valor_conciliado);
    }

    return null;
}

/**
 * Status da conciliação propriamente dita:
 * - "ok" / "incorreto" quando fechada (conciliado = true)
 * - "em_aberto" quando há itens e ainda não fechada
 * - null quando não há conciliação em andamento
 */
function getConciliacaoStatus(row) {
    const conciliacao = row?.conciliacao;
    if (!conciliacao) return null;

    const itens = getItens(row);
    const temItens = itens.length > 0;

    if (!conciliacao.conciliado) {
        return temItens ? "em_aberto" : null;
    }

    const somaCentavos = getSomaCentavos(row);
    const saldoCentavos = toCents(row?.saldo);
    if (somaCentavos == null || Number.isNaN(somaCentavos) || Number.isNaN(saldoCentavos)) {
        return "pendente";
    }

    return somaCentavos === saldoCentavos ? "ok" : "incorreto";
}

function isContaZerada(row) {
    const valores = [
        row?.debito,
        row?.credito,
        row?.movimento_mes,
        row?.saldo,
        row?.saldo_anterior,
    ];
    return valores.every((valor) => Math.round(Number(valor) * 100) === 0);
}

/**
 * Status de uma linha (ordem de prioridade):
 * - "ok":        fechada e a soma bate com o saldo atual
 * - "incorreto": fechada, mas a soma difere do saldo atual
 * - "em_aberto": tem itens salvos, ainda não fechada
 * - "sem_valor": não conciliada e sem nenhum valor (tudo zerado)
 * - "pendente":  não conciliada, mas com algum valor
 */
export function getContaStatus(row) {
    const conciliacao = getConciliacaoStatus(row);
    if (conciliacao === "ok" || conciliacao === "incorreto" || conciliacao === "em_aberto") {
        return conciliacao;
    }
    if (isContaZerada(row)) return "sem_valor";
    return "pendente";
}

/**
 * Indica se a conta é analítica (CT1_CLASSE = 2) — a única que é conciliada
 * diretamente. As sintéticas (CT1_CLASSE = 1) servem apenas de agrupamento.
 */
export function isAnalitica(row) {
    return Number(row?.classe) === 2;
}

/**
 * Calcula o status de cada conta considerando a hierarquia:
 * - Analítica (classe 2): status próprio.
 * - Sintética (classe 1): "ok" somente quando TODAS as analíticas abaixo
 *   estiverem "ok". Se alguma estiver "incorreto", propaga "incorreto";
 *   se alguma estiver "em_aberto", propaga "em_aberto"; senão "pendente".
 *
 * @param {Array<object>} rows - lista plana de contas
 * @returns {Map<string, string>} mapa conta => status
 */
export function buildStatusMap(rows) {
    const list = Array.isArray(rows) ? rows : [];
    const { childrenMap } = buildAccountTree(list);
    const statusMap = new Map();

    const collectAnalyticStatuses = (node, acc) => {
        if (Number(node.classe) === 2) {
            acc.push(getContaStatus(node));
        }
        (childrenMap.get(node.conta) || []).forEach((child) =>
            collectAnalyticStatuses(child, acc)
        );
        return acc;
    };

    list.forEach((row) => {
        if (Number(row.classe) === 1) {
            const statuses = [];
            (childrenMap.get(row.conta) || []).forEach((child) =>
                collectAnalyticStatuses(child, statuses)
            );

            let status;
            if (statuses.length === 0) {
                status = "pendente";
            } else if (statuses.every((s) => s === "ok")) {
                status = "ok";
            } else if (statuses.some((s) => s === "incorreto")) {
                status = "incorreto";
            } else if (statuses.some((s) => s === "em_aberto")) {
                status = "em_aberto";
            } else {
                status = "pendente";
            }

            statusMap.set(row.conta, status);
        } else {
            statusMap.set(row.conta, getContaStatus(row));
        }
    });

    return statusMap;
}
